/*
 * Created on Sat Apr 03 2021
 *
 * Copyright (c) 2021 Coding House LLC
 */
import { Recipe, RecipeIngredient, RecipeIngredients } from "../models/Recipe";
import { SpoonacularComplexSearchResults } from "../models/SpoonacularComplexSearchResults";
import {
    AccessModel,
    EntityAttachment,
    ContentReadResponse,
    EntityReference,
    TopicTagRef,
    RecipeReadResponse,
} from "../models/content";
import { BuilderEntitySearchResult } from "../screens/create/types";
import { getLocaleCode } from "../utils/Utilities";
import { StrongFoundationService } from "./StrongFoundationService";
import { DateTime } from "luxon";

export interface UserRecipeListItem {
    recipe?: Recipe;
    access: ContentReadResponse<Recipe>["access"];
    preview?: ContentReadResponse<Recipe>["preview"];
}

export interface RecipeSearchResult {
    id: string;
    title: string;
    image?: string;
}

export class RecipeService extends StrongFoundationService {
    public constructor() {
        super();
    }

    public fetchRecipeResponse = async (id: string): Promise<RecipeReadResponse | undefined> => {
        try {
            return await this.get<RecipeReadResponse>(`recipe/details`, {
                id,
                language: getLocaleCode(),
            });
        } catch (ex) {
            return undefined;
        }
    };

    public fetchRecipe = async (
        id: string,
    ): Promise<{ recipe: Recipe; ingredients?: RecipeIngredients } | undefined> => {
        try {
            const response = await this.fetchRecipeResponse(id);

            if (!response) {
                return undefined;
            }

            const recipe = response.recipe;

            if (!recipe) {
                return undefined;
            }

            const ingredients = await this.fetchRecipeIngredients(parseInt(id, 10));
            return { recipe, ingredients: ingredients };
        } catch (ex) {
            return undefined;
        }
    };
    public fetchRecipeIngredients = async (recipeId: number): Promise<RecipeIngredients | undefined> => {
        try {
            return await this.get("recipe/ingredients", { id: recipeId });
        } catch (ex) {
            return undefined;
        }
    };
    public fetchRecipes = async (afterDate: number, skip: number, isPublished = true): Promise<Recipe[]> => {
        try {
            const response = await this.get<Recipe[]>("recipe/recipes", {
                afterDate,
                skip,
                isPublished,
                language: getLocaleCode(),
            });
            return response ?? [];
        } catch (ex) {
            return [];
        }
    };

    public fetchUserRecipes = async (userId: string, afterDate = DateTime.utc().toMillis(), skip = 0): Promise<UserRecipeListItem[]> => {
        try {
            const response = await this.get<UserRecipeListItem[]>("recipe/user", {
                userId,
                afterDate,
                skip,
            });
            return response ?? [];
        } catch {
            return [];
        }
    };

    public createRecipe = async (input: {
        recipe: {
            title: string;
            summary: string;
            instructions: string;
            readyInMinutes: number | null;
            servings: number | null;
            image: string | null;
            analyzedInstructions: Array<{
                name: string;
                steps: Array<{ number: number; step: string }>;
            }>;
            extendedIngredients: RecipeIngredient[];
            creatorId: string;
            isPublished: true;
            attachments?: EntityAttachment[];
            topicTags: TopicTagRef[];
            entityRefs: EntityReference[];
            accessModel: AccessModel;
            dateCreated: number;
        };
        ingredients: RecipeIngredient[];
    }): Promise<{ id?: string; _id?: string } | undefined> => {
        return this.post<{ id?: string; _id?: string }>("recipe", input);
    };

    public searchRecipesByName = async (query: string): Promise<BuilderEntitySearchResult[]> => {
        if (!query.trim()) {
            return [];
        }
        try {
            const response = await this.get<SpoonacularComplexSearchResults>("recipe/search", {
                q: query,
                page: 0,
                language: getLocaleCode(),
                restrictions: [],
            });

            return (response?.results ?? []).map((recipe) => ({
                entityId: String(recipe.id),
                entityType: "recipe",
                displayName: recipe.title,
                subtitle: "Recipe",
                imageUrl: recipe.image,
            }));
        } catch {
            return [];
        }
    };

    public searchRecipes = async (query: string): Promise<RecipeSearchResult[]> => {
        if (!query.trim()) {
            return [];
        }

        try {
            const response = await this.get<SpoonacularComplexSearchResults>("recipe/search", {
                q: query,
                page: 0,
                language: getLocaleCode(),
                restrictions: [],
            });

            return (response?.results ?? []).map((recipe) => ({
                id: String(recipe.id),
                title: recipe.title,
                image: recipe.image,
            }));
        } catch {
            return [];
        }
    };
}

export const recipeService = new RecipeService();
